/**
 * Zapz storefront behaviour.
 *
 * Everything is event-delegated from `document`, so it keeps working when the theme editor
 * re-renders a section. State lives in the DOM (aria-pressed, hidden, output values) and the
 * markup is fully server-rendered, so the page is usable before this script runs.
 *
 * Cart writes go through the same contract the base theme's product form uses: a
 * CartLinesUpdateEvent carrying a promise, the cart endpoint, then a refresh of the cart items
 * component. That keeps the header cart bubble, the cart drawer and the cart page in sync.
 */

import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const config = window.Zapz || { strings: {}, routes: {} };
const strings = config.strings || {};
const FOCUS_KEY = 'zapz-focus';
const MAX_PER_FLAVOR = 9;

/* -------------------------------------------------------------------------- */
/* helpers                                                                     */
/* -------------------------------------------------------------------------- */

/** @param {number} cents */
const money = (cents) => formatMoney(cents, config.moneyFormat || '${{amount}}', config.currency || 'USD');

/** @param {string} selector @param {ParentNode} [root] */
const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

/** @param {number} n */
const plural = (n) => (n === 1 ? strings.pouch : strings.pouches) || (n === 1 ? 'pouch' : 'pouches');

/** @param {string} key @param {string | null} [value] */
function store(key, value) {
  try {
    if (value === undefined) return sessionStorage.getItem(key);
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch (_) {
    /* storage unavailable (private mode) — the page still works without it */
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* toast                                                                       */
/* -------------------------------------------------------------------------- */

let toastEl = null;
let toastTimer = 0;

/** @param {string} message @param {{ link?: boolean }} [options] */
function toast(message, options = {}) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'zz-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.hidden = true;
    document.body.appendChild(toastEl);
  }

  toastEl.textContent = '';
  const text = document.createElement('span');
  text.textContent = message;
  toastEl.appendChild(text);

  if (options.link && config.routes && config.routes.cart) {
    const link = document.createElement('a');
    link.href = config.routes.cart;
    link.textContent = strings.viewBag || 'View bag';
    // With the cart drawer enabled, open it through the header's own trigger instead of leaving the page.
    link.addEventListener('click', (event) => {
      const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (!(trigger instanceof HTMLElement)) return;
      event.preventDefault();
      trigger.click();
      if (toastEl) toastEl.hidden = true;
    });
    toastEl.appendChild(link);
  }

  toastEl.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (toastEl) toastEl.hidden = true;
  }, 3200);
}

/* -------------------------------------------------------------------------- */
/* cart                                                                        */
/* -------------------------------------------------------------------------- */

async function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems && typeof cartItems.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    return cartItems.fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Add one or more variants to the cart.
 * @param {{ id: string | number, quantity: number, selling_plan?: string | number }[]} items a line with a
 *   `selling_plan` is added as a subscription on that plan
 * @param {Element} [source] element the add originated from
 * @returns {Promise<boolean>} whether the add succeeded
 */
async function addToCart(items, source) {
  const lines = items.filter((item) => item.id && item.quantity > 0);
  if (!lines.length) return false;

  const sectionIds = $$('cart-items-component')
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const itemCount = lines.reduce((sum, item) => sum + item.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();
  const origin = source || document;

  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((item) => ({
          id: Number(item.id),
          quantity: item.quantity,
          ...(item.selling_plan ? { selling_plan: Number(item.selling_plan) } : {}),
        })),
        sections: sectionIds.join(','),
      }),
    });
    const result = await response.json();

    if (result.status) {
      origin.dispatchEvent(
        new CartErrorEvent({
          error: result.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: result.description, errors: result.errors },
        })
      );
      refreshCart()
        .then((cart) =>
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'zapz', itemCount },
          })
        )
        .catch(deferred.reject);
      toast(result.description || result.message || strings.error || 'Something went wrong');
      return false;
    }

    const cart = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'zapz',
        itemCount,
        sections: result.sections,
        didError: false,
      },
    });

    // The cart drawer opens by itself when the theme is set to "drawer"; only fall back to a toast
    // when there's no drawer to show the shopper what happened.
    if (!document.querySelector('cart-drawer-component[auto-open]')) {
      toast(strings.addedToBag || 'Added to your bag', { link: true });
    }
    return true;
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    origin.dispatchEvent(
      new CartErrorEvent({ error: (error && error.message) || 'Network error', code: 'SERVICE_UNAVAILABLE' })
    );
    toast(strings.error || 'Something went wrong');
    return false;
  }
}

/** Briefly mark a button as added. @param {HTMLElement} button */
function flashAdded(button) {
  const original = button.dataset.zzLabel || button.textContent;
  button.dataset.zzLabel = original || '';
  button.classList.add('is-added');
  button.textContent = strings.added || 'Added';
  window.setTimeout(() => {
    button.classList.remove('is-added');
    button.textContent = button.dataset.zzLabel || original || '';
  }, 1600);
}

/* -------------------------------------------------------------------------- */
/* shipping protection                                                         */
/* -------------------------------------------------------------------------- */

/*
 * Shipping protection is a normal product line. The cart shows it as a switch (never as a line with a quantity)
 * and this code keeps the cart honest:
 *   - a product was added and protection isn't in the cart, and the shopper hasn't switched it off  -> add one
 *   - protection is in the cart with nothing to protect                                             -> remove it
 *   - more than one protection line                                                                 -> make it one
 * Everything goes through /cart/update.js, which sets the line's quantity and stores the shopper's choice on the
 * cart in the same request. The choice therefore belongs to that cart: a new cart starts with protection on.
 * The cart UI is refreshed directly from the response. No Shopify standard event is dispatched for these
 * changes, so analytics and pixels only ever see the shopper's own adds and removals.
 */

const protection = config.protection || {};
const PROTECT_ATTRIBUTE = '_zapz_protection';
const protectionActive = () => Boolean(protection.variantId);

let protectChain = Promise.resolve();
let reconcileQueued = false;

/** Run protection work one step at a time. @param {() => Promise<unknown>} task */
function queueProtection(task) {
  protectChain = protectChain.then(task).catch((error) => {
    if (error?.name !== 'AbortError') console.warn('[zapz] shipping protection:', error);
  });
  return protectChain;
}

/**
 * Set the protection quantity (1 = on, 0 = off) and refresh the cart UI.
 * @param {number} quantity
 * @param {{ remember?: boolean }} [options] remember: store the shopper's on/off choice on the cart
 */
async function updateProtection(quantity, options = {}) {
  const cartItems = $$('cart-items-component').filter((el) => el instanceof HTMLElement && el.dataset.sectionId);
  const sectionIds = cartItems.map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId);

  /** @type {Record<string, unknown>} */
  const body = {
    updates: { [protection.variantId]: quantity },
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  };
  if (options.remember !== undefined) body.attributes = { [PROTECT_ATTRIBUTE]: options.remember ? 'on' : 'off' };

  const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const cart = await response.json();
  if (cart.status || cart.errors) throw new Error(cart.description || cart.message || 'Could not update the cart');

  const fee = (cart.items || []).find((item) => Number(item.variant_id) === protection.variantId);
  protection.inCart = fee ? fee.quantity : 0;
  const products = cart.item_count - protection.inCart;

  // Refresh every cart view on the page (drawer and/or cart page) from the rendered sections in the response.
  for (const el of cartItems) {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId || '';
    const html = cart.sections && cart.sections[id];
    if (!html) continue;
    sectionRenderer.abortRender(id);
    await morphSection(id, html, { mode: el.hasAttribute('data-drawer') ? 'hydration' : 'full' });
  }
  $$('cart-quantity-selector-component').forEach((selector) => {
    /** @type {any} */ (selector).updateButtonStates?.();
  });
  $$('cart-icon').forEach((icon) => {
    /** @type {any} */ (icon).renderCartBubble?.(products, false);
  });
  return true;
}

/** Look at the cart and add, remove or fix the protection line so it matches the rules above. */
async function reconcileProtection() {
  if (!protectionActive()) return;
  const cart = await refreshCart();
  const items = cart.items || [];
  const fee = items.find((item) => Number(item.variant_id) === protection.variantId);
  const others = items.filter((item) => item !== fee).reduce((sum, item) => sum + item.quantity, 0);
  const optedOut = Boolean(cart.attributes && cart.attributes[PROTECT_ATTRIBUTE] === 'off');

  if (fee && others === 0) await updateProtection(0);
  else if (fee && fee.quantity !== 1) await updateProtection(1);
  else if (!fee && others > 0 && protection.defaultOn !== false && !optedOut) await updateProtection(1);
}

function scheduleReconcile() {
  if (!protectionActive() || reconcileQueued) return;
  reconcileQueued = true;
  queueProtection(async () => {
    reconcileQueued = false;
    await reconcileProtection();
  });
}

// Every cart change made anywhere on the site (product page, quick add, this script, the cart itself) ends here.
if (protectionActive()) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        scheduleReconcile();
      })
      .catch(() => {});
  });

  // A cart left holding only the fee (or several of it) is tidied up as soon as any page loads.
  if ((protection.inCart > 0 && protection.othersInCart === 0) || protection.inCart > 1) scheduleReconcile();
}

/* -------------------------------------------------------------------------- */
/* candy burst                                                                 */
/* -------------------------------------------------------------------------- */

/** A short burst of the four candies from `host` (the open cart drawer, or the header's bag). @param {Element} host */
function candyBurst(host) {
  const candies = config.candies || [];
  if (reduceMotion || config.confetti === false || !candies.length || !host) return;

  const layer = document.createElement('div');
  layer.className = 'zz-confetti';
  layer.setAttribute('aria-hidden', 'true');
  const count = 16;
  for (let i = 0; i < count; i++) {
    const img = document.createElement('img');
    img.src = candies[i % candies.length];
    img.alt = '';
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
    const distance = 90 + Math.random() * 130;
    img.style.setProperty('--dx', `${Math.round(Math.cos(angle) * distance)}px`);
    img.style.setProperty('--dy', `${Math.round(Math.sin(angle) * distance - 40)}px`);
    img.style.setProperty('--rot', `${Math.round(Math.random() * 360 - 180)}deg`);
    img.style.setProperty('--size', `${18 + Math.round(Math.random() * 16)}px`);
    img.style.setProperty('--delay', `${Math.round(Math.random() * 120)}ms`);
    layer.appendChild(img);
  }
  host.appendChild(layer);
  window.setTimeout(() => layer.remove(), 1700);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;
  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      const icon = document.querySelector('.zz-header cart-icon, cart-icon');
      if (icon instanceof HTMLElement && !reduceMotion) restartClass(icon, 'zz-wiggle');
      // The drawer opens on this same event; wait for it rather than guessing how long that takes.
      const burstWhenOpen = (tries = 0) => {
        const drawer = document.querySelector('#cart-drawer dialog[open]');
        if (drawer) candyBurst(drawer);
        else if (tries < 14) window.setTimeout(() => burstWhenOpen(tries + 1), 80);
      };
      window.setTimeout(burstWhenOpen, 60);
    })
    .catch(() => {});
});

/* -------------------------------------------------------------------------- */
/* accent + flavor focus                                                       */
/* -------------------------------------------------------------------------- */

/** @param {Element} el an element carrying --main / --deep / --soft @param {boolean} [persist] remember it for the next page */
function setAccentFrom(el, persist = true) {
  const styles = getComputedStyle(el);
  const root = document.documentElement.style;
  const main = styles.getPropertyValue('--main').trim();
  const deep = styles.getPropertyValue('--deep').trim();
  const soft = styles.getPropertyValue('--soft').trim();
  if (main) root.setProperty('--a-main', main);
  if (deep) root.setProperty('--a-deep', deep);
  if (soft) root.setProperty('--a-soft', soft);
  if (persist && main && deep && soft) store('zapz-accent', JSON.stringify({ main, deep, soft }));
}

let currentFocus = null;
const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Whether the visitor has picked a flavor (clicked one, changed a quantity, added one). Until then, resting the
 * mouse on a flavor previews its colour. Remembered for the session, like the flavor itself.
 */
let flavorChosen = store('zapz-chosen') === '1';

function markFlavorChosen() {
  if (flavorChosen) return;
  flavorChosen = true;
  store('zapz-chosen', '1');
}

/** Restart a CSS animation class. @param {Element | null | undefined} el @param {string} cls */
function restartClass(el, cls) {
  if (!el) return;
  el.classList.remove(cls);
  void /** @type {HTMLElement} */ (el).offsetWidth;
  el.classList.add(cls);
}

/**
 * Show the flavor with slot `index` everywhere on the page.
 * Options: `picked` reveals the landing hero's photo thumbnails; `fx` runs the landing hero's flavor
 * change animation ("user" floods from the clicked swatch, "auto" from the centre). Automatic changes
 * are not remembered as the visitor's choice. `preview` marks a change made by resting the mouse on a flavor
 * and `init` the first paint of the page: neither counts as the visitor choosing a flavor.
 * @param {string} index
 * @param {Element} [from]
 * @param {{ picked?: boolean, fx?: 'user' | 'auto', preview?: boolean, init?: boolean }} [opts]
 */
function focusFlavor(index, from, opts = {}) {
  const previous = currentFocus;
  currentFocus = index;
  if (!opts.preview && !opts.init && opts.fx !== 'auto') markFlavorChosen();
  const source = from || document.querySelector(`[data-zz-focus="${index}"]`);
  const persist = opts.fx !== 'auto';
  if (source) setAccentFrom(source, persist);

  $$('[data-zz-focus]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.getAttribute('data-zz-focus') === index));
  });
  $$('[data-zz-when-focus]').forEach((el) => {
    el.hidden = el.getAttribute('data-zz-when-focus') !== index;
  });
  if (persist) store(FOCUS_KEY, index);

  $$('[data-zz-lhero]').forEach((hero) => {
    if (!hero.querySelector(`[data-zz-focus="${index}"]`)) return;
    showHeroMedia(hero, index, 'm0');
    if (opts.picked) hero.classList.add('is-picked');
    if (opts.fx && previous !== index && !reduceMotion) heroFx(hero, index, opts.fx === 'user' ? source : null);
  });
}

/* -------------------------------------------------------------------------- */
/* landing hero                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Show one of a flavor's photos in the hero card. Photos are keyed "m0", "m1", ... (the product's media in
 * order), "cutout" and "lineup". Thumbnails for the lineup carry data-flavor="*" because that photo belongs to
 * every flavor.
 * @param {Element} hero @param {string} index @param {string | number} k
 */
function showHeroMedia(hero, index, k) {
  $$('[data-zz-hero-media]', hero).forEach((el) => {
    const [flavor, photo] = (el.getAttribute('data-zz-hero-media') || '').split(':');
    const show = flavor === String(index) && photo === String(k);
    if (!show) el.querySelectorAll('video').forEach((video) => video.pause());
    el.hidden = !show;
  });
  $$('[data-zz-act="hero-media"]', hero).forEach((thumb) => {
    const owner = thumb.getAttribute('data-flavor');
    const on = (owner === String(index) || owner === '*') && thumb.getAttribute('data-k') === String(k);
    thumb.setAttribute('aria-pressed', String(on));
  });
}

/** The flavor the landing hero is showing right now. @param {Element} hero */
function heroFlavor(hero) {
  return hero.querySelector('[data-zz-hero-pick][aria-pressed="true"]')?.getAttribute('data-zz-focus') || currentFocus || '1';
}

/** The flavor change animation: the hero floods with the new colour and the headline word swaps. */
function heroFx(hero, index, origin) {
  const rotator = $$('.zz-lhero__rotator', hero).find((el) => !el.hidden);
  restartClass(rotator, 'is-swap');
  restartClass(hero.querySelector('.zz-lhero__card'), 'is-pop');

  const flood = hero.querySelector('.zz-lhero__flood');
  const swatch = hero.querySelector(`[data-zz-focus="${index}"]`);
  if (!(flood instanceof HTMLElement) || !swatch) return;
  const box = hero.getBoundingClientRect();
  let x = 50;
  let y = 50;
  if (origin instanceof Element) {
    const r = origin.getBoundingClientRect();
    x = ((r.left + r.width / 2 - box.left) / box.width) * 100;
    y = ((r.top + r.height / 2 - box.top) / box.height) * 100;
  }
  flood.style.setProperty('--fx', `${x}%`);
  flood.style.setProperty('--fy', `${y}%`);
  flood.style.background = getComputedStyle(swatch).getPropertyValue('--deep').trim() || 'transparent';
  restartClass(flood, 'is-on');
  flood.onanimationend = () => {
    flood.classList.remove('is-on');
    flood.style.background = 'transparent';
  };
}

let heroTimer = 0;
let heroStartTimer = 0;

/** After the CSS intro has played, stop it replaying whenever a flavor's elements are shown again. @param {Element} hero */
function markIntroDone(hero) {
  if (!hero.classList.contains('zz-lhero--intro') || hero.classList.contains('is-intro-done')) return;
  window.setTimeout(() => hero.classList.add('is-intro-done'), 2800);
}

function heroAutoStop() {
  window.clearInterval(heroTimer);
  window.clearTimeout(heroStartTimer);
  heroTimer = 0;
  heroStartTimer = 0;
}

/** Rotate through the flavors until the visitor interacts. @param {Element} hero */
function heroAutoStart(hero) {
  heroAutoStop();
  markIntroDone(hero);
  if (reduceMotion || hero.getAttribute('data-zz-auto') !== 'true') return;
  if ($$('[data-zz-hero-pick]', hero).length < 2) return;
  const interval = Number(hero.getAttribute('data-zz-interval')) || 3500;

  const begin = () => {
    heroTimer = window.setInterval(() => {
      if (!document.body.contains(hero)) return heroAutoStop();
      // Pause while the tab is hidden or the hero has mostly scrolled out of view. The base theme scrolls an
      // inner wrapper rather than the window, so measure the hero itself.
      const box = hero.getBoundingClientRect();
      if (document.hidden || box.bottom < window.innerHeight * 0.4 || box.top > window.innerHeight) return;
      const picks = $$('[data-zz-hero-pick]', hero);
      const current = picks.findIndex((pick) => pick.getAttribute('aria-pressed') === 'true');
      const next = picks[(current + 1) % picks.length];
      if (next) focusFlavor(next.getAttribute('data-zz-focus') || '1', next, { fx: 'auto' });
    }, interval);
  };

  // Let the intro animation finish before the first change.
  if (hero.getAttribute('data-zz-intro') === 'true') heroStartTimer = window.setTimeout(begin, 1800);
  else begin();
}

/* -------------------------------------------------------------------------- */
/* goal finder: quantity + variant                                             */
/* -------------------------------------------------------------------------- */

/** @param {Element} panel */
function updatePanel(panel) {
  const qtyOutput = panel.querySelector('[data-zz-qty]');
  const add = panel.querySelector('[data-zz-act="add"]');
  if (!qtyOutput || !add) return;

  const qty = Math.max(1, parseInt(qtyOutput.textContent || '1', 10));
  const select = panel.querySelector('select[data-zz-variant]');
  let price = parseInt(add.getAttribute('data-price') || '0', 10);
  let available = add.getAttribute('data-available') !== 'false';

  if (select instanceof HTMLSelectElement) {
    const option = select.selectedOptions[0];
    add.setAttribute('data-variant', select.value);
    price = parseInt(option.getAttribute('data-price') || '0', 10);
    available = option.getAttribute('data-available') !== 'false';
    const priceEl = panel.querySelector('[data-zz-price]');
    if (priceEl) priceEl.textContent = money(price);
  }

  add.setAttribute('data-qty', String(qty));
  if (add instanceof HTMLButtonElement) add.disabled = !available;
  add.textContent = available
    ? `${strings.addToBag || 'Add to bag'} · ${money(price * qty)}`
    : strings.soldOut || 'Sold out';
  add.dataset.zzLabel = add.textContent || '';
}

/* -------------------------------------------------------------------------- */
/* pack builder                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Whether the shopper chose to subscribe in this pack builder's purchase options.
 * @param {Element} root
 */
function packSubscribing(root) {
  const input = root.querySelector('[data-zz-purchase] input[value="subscribe"]');
  return input instanceof HTMLInputElement && input.checked;
}

/** @param {Element} root */
function renderPack(root) {
  const goals = $$('[data-zz-goal]', root);
  const subscribing = packSubscribing(root);
  let total = 0;
  let cents = 0;
  let oneQty = 0; // pouches bought once: the multi-pouch discount counts these
  let oneCents = 0;
  let subQty = 0; // pouches bought on the subscription plan
  let subSaved = 0; // what the plan takes off those pouches
  /** @type {string[]} */
  const colors = [];

  goals.forEach((goal) => {
    const count = parseInt(goal.querySelector('[data-zz-count]')?.textContent || '0', 10);
    const badge = goal.querySelector('[data-zz-badge]');
    if (badge) badge.textContent = count ? String(count) : '';
    const price = parseInt(goal.getAttribute('data-price') || '0', 10);
    const planPrice = parseInt(goal.getAttribute('data-plan-price') || '0', 10);
    total += count;
    if (subscribing && goal.hasAttribute('data-plan') && planPrice > 0) {
      subQty += count;
      cents += count * planPrice;
      subSaved += count * Math.max(0, price - planPrice);
    } else {
      oneQty += count;
      oneCents += count * price;
      cents += count * price;
    }
    const main = getComputedStyle(goal).getPropertyValue('--main').trim();
    for (let i = 0; i < count; i++) colors.push(main);
  });

  const meter = root.querySelector('[data-zz-meter]');
  if (meter) {
    meter.textContent = '';
    for (let i = 0; i < Math.max(4, total); i++) {
      const bar = document.createElement('span');
      if (colors[i]) bar.style.background = colors[i];
      meter.appendChild(bar);
    }
  }

  // The multi-pouch offer. The discount itself is applied by the shop's automatic discount at checkout; this
  // only shows the shopper an estimate of it. It counts pouches bought once, because subscription pouches get the
  // plan's price instead.
  const offers = config.offers || {};
  const percent = Number(offers.bundlePercent) || 0;
  const needed = Number(offers.bundleQty) || 2;
  const reached = percent > 0 && oneQty >= needed;
  const saved = reached && offers.estimate !== false ? Math.round((oneCents * percent) / 100) : 0;

  const purchase = root.querySelector('[data-zz-purchase]');
  const offerLine = root.querySelector('[data-zz-offer]');
  const offerText = offerLine?.querySelector('[data-zz-offer-text]');
  if (offerLine && offerText) {
    let text = offers.bundle || '';
    let good = reached;
    if (subscribing && subQty > 0 && oneQty === 0) {
      // Everything is on the plan: its own saving is what counts.
      const planPercent = purchase?.getAttribute('data-percent') || '0';
      good = subSaved > 0 && !!strings.subscribedSaving;
      text = good ? strings.subscribedSaving.replace('[percent]', planPercent).replace('[amount]', money(subSaved)) : '';
    } else if (subscribing && total === 0) {
      text = purchase?.getAttribute('data-hint') || text;
    } else if (oneQty > 0 && oneQty < needed && percent > 0) {
      text = (offers.more || text).replace('[count]', String(needed - oneQty));
    } else if (reached) {
      text = offers.done || text;
      if (saved > 0 && strings.youSave) text += ` · ${strings.youSave.replace('[amount]', money(saved))}`;
    }
    offerText.textContent = text;
    if (offerLine instanceof HTMLElement) offerLine.hidden = !text;
    offerLine.classList.toggle('is-reached', good);
  }

  const add = root.querySelector('[data-zz-pack-add]');
  if (add instanceof HTMLButtonElement) {
    const verb = subQty > 0 && oneQty === 0 ? strings.subscribeToBag || 'Subscribe and add' : strings.addToBag || 'Add to bag';
    add.disabled = total === 0;
    add.textContent =
      total === 0
        ? strings.pickFlavor || 'Pick at least one flavor'
        : `${verb} ${total} ${plural(total)} · ${money(cents - saved)}`;
  }
}

/** Show a stage view: a flavor index or "lineup". @param {Element} root @param {string} view */
function showStage(root, view) {
  $$('[data-zz-stage-card]', root).forEach((card) => {
    card.hidden = card.getAttribute('data-zz-stage-card') !== view;
  });
  const active = root.querySelector(`[data-zz-stage-card="${view}"]`);
  const badge = root.querySelector('[data-zz-stage-badge]');
  if (badge) {
    const label = active?.getAttribute('data-badge') || '';
    badge.textContent = label;
    badge.hidden = !label;
  }
  $$('[data-zz-act="view"]', root).forEach((thumb) => {
    thumb.setAttribute('aria-pressed', String(thumb.getAttribute('data-view') === view));
  });
}

/* -------------------------------------------------------------------------- */
/* reviews: filter by flavor, show more                                        */
/* -------------------------------------------------------------------------- */

/**
 * Show the reviews that match the chosen flavor, a batch at a time. Reviews written about no flavor in
 * particular always match.
 * @param {Element} box the [data-zz-reviews] wrapper
 */
function applyReviews(box) {
  const filter = box.getAttribute('data-zz-filter') || '0';
  const page = Number(box.getAttribute('data-zz-page')) || 9;
  const shown = Number(box.getAttribute('data-zz-shown')) || page;
  const cards = $$('[data-zz-review]', box);
  const matching = cards.filter((card) => {
    const flavor = card.getAttribute('data-flavor');
    return filter === '0' || flavor === filter || flavor === '0';
  });
  cards.forEach((card) => {
    card.hidden = !(matching.includes(card) && matching.indexOf(card) < shown);
  });
  const more = box.querySelector('.zz-rv__more');
  if (more instanceof HTMLElement) more.hidden = matching.length <= shown;
  $$('[data-zz-act="rv-filter"]', box).forEach((chip) => {
    chip.setAttribute('aria-pressed', String(chip.getAttribute('data-flavor') === filter));
  });
}

/* -------------------------------------------------------------------------- */
/* lightbox: media wall photos and review photos                               */
/* -------------------------------------------------------------------------- */

/** @type {HTMLDialogElement | null} */
let lightbox = null;
/** @type {HTMLElement[]} */
let lightboxItems = [];
let lightboxIndex = 0;

function ensureLightbox() {
  if (lightbox) return lightbox;
  const dialog = document.createElement('dialog');
  dialog.className = 'zz zz-lightbox';
  dialog.setAttribute('aria-label', strings.photos || 'Photos');
  dialog.innerHTML = `
    <button type="button" class="zz-lightbox__close" data-zz-lb="close" aria-label="${strings.close || 'Close'}">&times;</button>
    <button type="button" class="zz-lightbox__nav zz-lightbox__nav--prev" data-zz-lb="prev" aria-label="${strings.previous || 'Previous'}">&lsaquo;</button>
    <figure class="zz-lightbox__figure"><div class="zz-lightbox__media"></div><figcaption></figcaption></figure>
    <button type="button" class="zz-lightbox__nav zz-lightbox__nav--next" data-zz-lb="next" aria-label="${strings.next || 'Next'}">&rsaquo;</button>`;
  document.body.appendChild(dialog);
  dialog.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const action = t.closest('[data-zz-lb]')?.getAttribute('data-zz-lb');
    if (action === 'close' || t === dialog) dialog.close();
    else if (action === 'prev') stepLightbox(-1);
    else if (action === 'next') stepLightbox(1);
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') stepLightbox(-1);
    if (event.key === 'ArrowRight') stepLightbox(1);
  });
  dialog.addEventListener('close', () => {
    const media = dialog.querySelector('.zz-lightbox__media');
    if (media) media.innerHTML = '';
  });
  lightbox = dialog;
  return dialog;
}

function showLightboxItem() {
  const dialog = ensureLightbox();
  const tile = lightboxItems[lightboxIndex];
  const media = dialog.querySelector('.zz-lightbox__media');
  const caption = dialog.querySelector('figcaption');
  if (!tile || !media || !caption) return;
  media.innerHTML = '';
  const videoSrc = tile.getAttribute('data-zz-video');
  const embedSrc = tile.getAttribute('data-zz-embed');
  /** @type {HTMLElement} */
  let node;
  if (videoSrc) {
    const video = document.createElement('video');
    video.src = videoSrc;
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    node = video;
  } else if (embedSrc) {
    const frame = document.createElement('iframe');
    frame.src = embedSrc;
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.allowFullscreen = true;
    frame.title = tile.getAttribute('data-zz-alt') || 'Video';
    node = frame;
  } else {
    const img = document.createElement('img');
    img.src = tile.getAttribute('data-zz-full') || '';
    img.alt = tile.getAttribute('data-zz-alt') || '';
    node = img;
  }
  media.appendChild(node);
  caption.textContent = tile.getAttribute('data-zz-caption') || tile.getAttribute('data-zz-alt') || '';
  const many = lightboxItems.length > 1;
  dialog.querySelectorAll('.zz-lightbox__nav').forEach((nav) => {
    /** @type {HTMLElement} */ (nav).hidden = !many;
  });
}

/** @param {number} direction */
function stepLightbox(direction) {
  if (lightboxItems.length < 2) return;
  lightboxIndex = (lightboxIndex + direction + lightboxItems.length) % lightboxItems.length;
  showLightboxItem();
}

/** Open the lightbox on `tile`; previous / next walk the tiles in the same [data-zz-zoom-group]. @param {HTMLElement} tile */
function openLightbox(tile) {
  const group = tile.closest('[data-zz-zoom-group]') || document;
  lightboxItems = /** @type {HTMLElement[]} */ ($$('[data-zz-zoom]', group).filter((item) => !item.closest('[hidden]')));
  lightboxIndex = Math.max(0, lightboxItems.indexOf(tile));
  const dialog = ensureLightbox();
  showLightboxItem();
  if (!dialog.open) dialog.showModal();
}

/* -------------------------------------------------------------------------- */
/* click handling                                                              */
/* -------------------------------------------------------------------------- */

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;

  // In-page links (e.g. the landing hero button) scroll smoothly to their target.
  const scrollLink = target?.closest('a[data-zz-scroll]');
  if (scrollLink) {
    const dest = document.getElementById((scrollLink.getAttribute('href') || '').slice(1));
    if (dest) {
      event.preventDefault();
      heroAutoStop();
      dest.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
    return;
  }

  // Photos in the media wall and on reviews open in the lightbox.
  const zoomTile = target?.closest('[data-zz-zoom]');
  if (zoomTile instanceof HTMLElement) {
    event.preventDefault();
    openLightbox(zoomTile);
    return;
  }

  const el = target?.closest('[data-zz-act], [data-zz-focus]');
  if (!(el instanceof HTMLElement)) return;

  if (el.hasAttribute('data-zz-focus') && !el.hasAttribute('data-zz-act')) {
    const index = el.getAttribute('data-zz-focus') || '1';
    if (el.hasAttribute('data-zz-hero-pick')) {
      heroAutoStop();
      focusFlavor(index, el, { picked: true, fx: 'user' });
    } else {
      focusFlavor(index, el);
    }
    return;
  }

  const act = el.getAttribute('data-zz-act');

  switch (act) {
    case 'qty': {
      const panel = el.closest('[data-zz-panel]');
      const output = panel?.querySelector('[data-zz-qty]');
      if (!panel || !output) return;
      const next = Math.max(1, Math.min(MAX_PER_FLAVOR, parseInt(output.textContent || '1', 10) + Number(el.dataset.n)));
      output.textContent = String(next);
      updatePanel(panel);
      break;
    }

    case 'add': {
      if (!(el instanceof HTMLButtonElement) || el.disabled) return;
      const id = el.getAttribute('data-variant');
      const qty = parseInt(el.getAttribute('data-qty') || '1', 10);
      const focusIndex = el.getAttribute('data-focus');
      if (focusIndex) focusFlavor(focusIndex, el.closest('[style]') || el);
      el.setAttribute('aria-busy', 'true');
      const ok = await addToCart([{ id: id || '', quantity: qty }], el);
      el.removeAttribute('aria-busy');
      if (ok && el.classList.contains('zz-tile__add')) flashAdded(el);
      break;
    }

    case 'step': {
      const root = el.closest('[data-zz-pack]');
      const goal = el.closest('[data-zz-goal]');
      const output = goal?.querySelector('[data-zz-count]');
      if (!root || !goal || !output) return;
      const next = Math.max(0, Math.min(MAX_PER_FLAVOR, parseInt(output.textContent || '0', 10) + Number(el.dataset.n)));
      output.textContent = String(next);
      const index = goal.getAttribute('data-zz-goal') || '1';
      heroAutoStop();
      focusFlavor(index, goal, { picked: true });
      showStage(root, index);
      renderPack(root);
      break;
    }

    case 'view': {
      const root = el.closest('[data-zz-pack]');
      if (!root) return;
      const view = el.getAttribute('data-view') || '1';
      if (view !== 'lineup') {
        heroAutoStop();
        focusFlavor(view, el, { picked: true });
      }
      showStage(root, view);
      break;
    }

    case 'hero-media': {
      const hero = el.closest('[data-zz-lhero]');
      if (!hero) return;
      heroAutoStop();
      // The photo rail lists every flavor, so tapping another flavor's photo switches the whole page to it.
      const owner = el.getAttribute('data-flavor');
      const flavor = !owner || owner === '*' ? heroFlavor(hero) : owner;
      if (flavor !== heroFlavor(hero)) focusFlavor(flavor, hero.querySelector(`[data-zz-hero-pick][data-zz-focus="${flavor}"]`) || el, { picked: true, fx: 'user' });
      showHeroMedia(hero, flavor, el.getAttribute('data-k') || 'm0');
      if (!reduceMotion) restartClass(hero.querySelector('.zz-lhero__card'), 'is-pop');
      break;
    }

    case 'pack-add': {
      const root = el.closest('[data-zz-pack]');
      if (!root || !(el instanceof HTMLButtonElement) || el.disabled) return;
      // Subscribing adds each pouch on its own plan from the subscription app, exactly as its product page would.
      const subscribing = packSubscribing(root);
      const items = $$('[data-zz-goal]', root).map((goal) => {
        const plan = subscribing ? goal.getAttribute('data-plan') : null;
        return {
          id: goal.getAttribute('data-variant') || '',
          quantity: parseInt(goal.querySelector('[data-zz-count]')?.textContent || '0', 10),
          ...(plan ? { selling_plan: plan } : {}),
        };
      });
      el.setAttribute('aria-busy', 'true');
      await addToCart(items, el);
      el.removeAttribute('aria-busy');
      break;
    }

    case 'protect-toggle': {
      if (!(el instanceof HTMLButtonElement) || !protectionActive()) return;
      const turnOn = el.getAttribute('aria-checked') !== 'true';
      const card = el.closest('[data-zz-protect]');
      // Flip the switch straight away; the cart re-renders with the real state a moment later.
      el.setAttribute('aria-checked', String(turnOn));
      card?.classList.toggle('is-on', turnOn);
      if (turnOn && !reduceMotion) restartClass(card, 'is-popping');
      queueProtection(() => updateProtection(turnOn ? 1 : 0, { remember: turnOn })).then(() => {
        // If the request failed the cart was not changed: put the switch back.
        if ((protection.inCart > 0) !== turnOn && document.body.contains(el)) {
          el.setAttribute('aria-checked', String(!turnOn));
          card?.classList.toggle('is-on', !turnOn);
        }
      });
      break;
    }

    case 'rv-filter': {
      const box = el.closest('[data-zz-reviews]');
      if (!box) return;
      box.setAttribute('data-zz-filter', el.getAttribute('data-flavor') || '0');
      box.removeAttribute('data-zz-shown');
      applyReviews(box);
      break;
    }

    case 'rv-more': {
      const box = el.closest('[data-zz-reviews]');
      if (!box) return;
      const page = Number(box.getAttribute('data-zz-page')) || 9;
      const shown = (Number(box.getAttribute('data-zz-shown')) || page) + page;
      box.setAttribute('data-zz-shown', String(shown));
      applyReviews(box);
      break;
    }

    case 'tab': {
      const list = el.closest('[role="tablist"]');
      if (!list) return;
      $$('[role="tab"]', list).forEach((tab) => {
        const selected = tab === el;
        tab.setAttribute('aria-selected', String(selected));
        const panel = document.getElementById(tab.getAttribute('aria-controls') || '');
        if (panel) panel.hidden = !selected;
      });
      break;
    }

    case 'ritual-time': {
      const root = el.closest('[data-zz-ritual]');
      if (!root) return;
      const time = el.getAttribute('data-time');
      $$('[data-zz-act="ritual-time"]', root).forEach((button) => {
        button.setAttribute('aria-pressed', String(button === el));
      });
      $$('[data-zz-ritual-cards]', root).forEach((group) => {
        group.hidden = group.getAttribute('data-zz-ritual-cards') !== time;
      });
      break;
    }

    case 'ritual-add': {
      const root = el.closest('[data-zz-ritual]');
      if (!root || !(el instanceof HTMLButtonElement)) return;
      const group = $$('[data-zz-ritual-cards]', root).find((g) => !g.hidden);
      const items = group
        ? $$('[data-zz-act="add"]', group)
            .filter((button) => !(button instanceof HTMLButtonElement && button.disabled))
            .map((button) => ({ id: button.getAttribute('data-variant') || '', quantity: 1 }))
        : [];
      el.setAttribute('aria-busy', 'true');
      await addToCart(items, el);
      el.removeAttribute('aria-busy');
      break;
    }

    case 'add-all': {
      const scope = el.closest('[data-zz-add-all-scope]') || document;
      const items = $$('[data-zz-add-all-item]', scope).map((item) => ({
        id: item.getAttribute('data-variant') || '',
        quantity: 1,
      }));
      el.setAttribute('aria-busy', 'true');
      const ok = await addToCart(items, el);
      el.removeAttribute('aria-busy');
      if (ok && el instanceof HTMLElement) flashAdded(el);
      break;
    }

    case 'video': {
      const card = el.closest('[data-zz-video]');
      if (!(card instanceof HTMLElement)) return;
      const file = card.getAttribute('data-src');
      const embed = card.getAttribute('data-embed');
      let player = null;
      if (file) {
        player = document.createElement('video');
        player.src = file;
        player.controls = true;
        player.autoplay = true;
        player.playsInline = true;
      } else if (embed) {
        player = document.createElement('iframe');
        player.src = embed;
        player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        player.allowFullscreen = true;
        player.title = card.getAttribute('data-title') || 'Video';
      }
      if (player) {
        card.appendChild(player);
        el.remove();
      }
      break;
    }

    case 'menu': {
      const drawer = document.getElementById('zz-drawer');
      if (!drawer) return;
      drawer.hidden = !drawer.hidden;
      el.setAttribute('aria-expanded', String(!drawer.hidden));
      break;
    }

    default:
      break;
  }
});

document.addEventListener('change', (event) => {
  const target = event.target;

  // One-time or subscription in the pack builder: the totals and the button follow.
  if (target instanceof HTMLInputElement && target.closest('[data-zz-purchase]')) {
    const pack = target.closest('[data-zz-pack]');
    if (pack) renderPack(pack);
    return;
  }

  if (!(target instanceof HTMLSelectElement)) return;

  if (target.hasAttribute('data-zz-variant')) {
    const panel = target.closest('[data-zz-panel]');
    if (panel) updatePanel(panel);
  } else if (target.hasAttribute('data-zz-sort')) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', target.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }
});

/* -------------------------------------------------------------------------- */
/* hover preview                                                               */
/* -------------------------------------------------------------------------- */

/*
 * Until the visitor has picked a flavor, resting the mouse on one previews it: the colour of the page, the
 * landing hero and the flavor banner change to that flavor, and go back when the mouse leaves. Once they have
 * picked one, hovering does nothing. Touch screens have no hover, so they are left out.
 */
const HOVER_SELECTOR = '[data-zz-hero-pick], [data-zz-hover-flavor]';
/** The flavor that was showing before the preview started. @type {string | null} */
let hoverBase = null;
/** The accent colours that were showing before the preview started. @type {Record<string, string>} */
let hoverAccent = {};
let hoverTimer = 0;

/** @param {Element} target */
function previewFlavor(target) {
  const index = target.getAttribute('data-zz-focus') || target.getAttribute('data-zz-hover-flavor');
  if (!index) return;
  window.clearTimeout(hoverTimer);
  if (hoverBase === null) {
    hoverBase = currentFocus || '1';
    const root = document.documentElement.style;
    hoverAccent = {
      '--a-main': root.getPropertyValue('--a-main'),
      '--a-deep': root.getPropertyValue('--a-deep'),
      '--a-soft': root.getPropertyValue('--a-soft'),
    };
  }
  if (index === currentFocus) return;
  heroAutoStop();
  focusFlavor(index, target, { fx: 'auto', preview: true });
}

/** The mouse left the flavor: put the page back as it was, unless it moved straight onto another flavor. */
function endFlavorPreview() {
  window.clearTimeout(hoverTimer);
  hoverTimer = window.setTimeout(() => {
    if (hoverBase === null) return;
    const base = hoverBase;
    hoverBase = null;
    if (flavorChosen) return;
    if (base !== currentFocus) focusFlavor(base, undefined, { fx: 'auto', preview: true });
    // The base flavor may have no button on this page to read its colours from, so put them back directly.
    const root = document.documentElement.style;
    Object.entries(hoverAccent).forEach(([name, value]) => {
      if (value) root.setProperty(name, value);
    });
    $$('[data-zz-lhero]').forEach((hero) => heroAutoStart(hero));
  }, 160);
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse' || flavorChosen) return;
  const target = event.target instanceof Element ? event.target.closest(HOVER_SELECTOR) : null;
  if (target) previewFlavor(target);
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse' || hoverBase === null) return;
  const target = event.target instanceof Element ? event.target.closest(HOVER_SELECTOR) : null;
  if (!target) return;
  const next = event.relatedTarget instanceof Element ? event.relatedTarget.closest(HOVER_SELECTOR) : null;
  if (next === target) return; // still inside the same flavor
  endFlavorPreview();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const drawer = document.getElementById('zz-drawer');
  if (drawer && !drawer.hidden) {
    drawer.hidden = true;
    const button = document.querySelector('[data-zz-act="menu"]');
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-expanded', 'false');
      button.focus();
    }
  }
});

/* -------------------------------------------------------------------------- */
/* init                                                                        */
/* -------------------------------------------------------------------------- */

/** @type {ResizeObserver | null} */
let headerObserver = null;

function initHeaderHeights() {
  const header = document.querySelector('.zz-header-section');
  if (!header) return;
  const row = header.querySelector('.zz-header');
  const apply = () => {
    const height = header.getBoundingClientRect().height;
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
    // The header row alone: how far a hero has to reach up behind a transparent header.
    if (row) document.documentElement.style.setProperty('--zz-hdr-h', `${row.getBoundingClientRect().height}px`);
  };
  apply();
  if ('ResizeObserver' in window) {
    headerObserver?.disconnect();
    headerObserver = new ResizeObserver(apply);
    headerObserver.observe(header);
  }
  updateHeaderState();
}

/**
 * A transparent header turns solid once the page has scrolled. Desktop scrolls an inner wrapper and mobile
 * scrolls the window, so both are checked.
 */
function updateHeaderState() {
  const header = document.querySelector('.zz-header--transparent');
  if (!header) return;
  const wrapper = document.querySelector('.page-wrapper');
  const top = Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
  header.classList.toggle('is-solid', top > 12);
}

// Scroll events don't bubble, so listen while they travel down to whatever element is scrolling.
document.addEventListener('scroll', updateHeaderState, { capture: true, passive: true });
window.addEventListener('pageshow', updateHeaderState);

/** Prepare all server-rendered widgets inside `root`. @param {ParentNode} root */
function initWidgets(root) {
  $$('[data-zz-panel]', root).forEach(updatePanel);
  $$('[data-zz-pack]', root).forEach((pack) => {
    const first = pack.querySelector('[data-zz-goal]');
    showStage(pack, first?.getAttribute('data-zz-goal') || 'lineup');
    renderPack(pack);
  });
  $$('[data-zz-reviews]', root).forEach(applyReviews);
  // A product page belongs to its own flavor, so it wins over the flavor the shopper chose earlier. When the
  // page has no flavor buttons the accent set in <head> is already the right one.
  const productFlavor = config.productFlavor ? String(config.productFlavor) : '';
  const stored = productFlavor || store(FOCUS_KEY);
  const known = stored && document.querySelector(`[data-zz-focus="${stored}"]`);
  const first = document.querySelector('[data-zz-focus]');
  if (known) focusFlavor(stored, undefined, { init: true });
  else if (!productFlavor && first) focusFlavor(first.getAttribute('data-zz-focus') || '1', first, { init: true });
  $$('[data-zz-lhero]', root).forEach((hero) => heroAutoStart(hero));
}

initHeaderHeights();
initWidgets(document);

// Theme editor: re-run setup when a section is (re)rendered, and follow the selected section.
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target instanceof Element ? event.target : document;
  initWidgets(root);
  if (currentFocus) focusFlavor(currentFocus);
  initHeaderHeights();
});
